import React from "react";

interface EngineeringIconProps {
  type: "gear" | "pulley" | "wrench" | "cog" | "chain" | "bolt" | "screw" | "hammer";
  size?: "sm" | "md" | "lg";
  className?: string;
  animated?: boolean;
}

const EngineeringIcon: React.FC<EngineeringIconProps> = ({ 
  type, 
  size = "md", 
  className = "",
  animated = true 
}) => {
  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-12 h-12"
  };

  const animationClasses = animated ? "animate-spin" : "";

  const icons = {
    gear: (
      <svg 
        className={`${sizeClasses[size]} ${animationClasses} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
        <path d="M12 4c-4.41 0-8 3.59-8 8s3.59 8 8 8 8-3.59 8-8-3.59-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>
      </svg>
    ),
    pulley: (
      <svg 
        className={`${sizeClasses[size]} ${animationClasses} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
        <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
      </svg>
    ),
    wrench: (
      <svg 
        className={`${sizeClasses[size]} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
      </svg>
    ),
    cog: (
      <svg 
        className={`${sizeClasses[size]} ${animationClasses} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.22-.08-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65z"/>
      </svg>
    ),
    chain: (
      <svg 
        className={`${sizeClasses[size]} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
      </svg>
    ),
    bolt: (
      <svg 
        className={`${sizeClasses[size]} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
      </svg>
    ),
    screw: (
      <svg 
        className={`${sizeClasses[size]} ${animationClasses} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"/>
      </svg>
    ),
    hammer: (
      <svg 
        className={`${sizeClasses[size]} ${className}`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M22 19L13 10L15 8L22 15L22 19Z"/>
        <path d="M2 5L9 12L7 14L2 9L2 5Z"/>
        <path d="M15 8L13 10L15 12L17 10L15 8Z"/>
      </svg>
    )
  };

  return icons[type] || icons.gear;
};

export default EngineeringIcon; 